
import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth } from "@/lib/auth/auth-context"
import type { Store } from "@/lib/types/database"
import { useMyTeams, useGroupDealerships } from "@/lib/api/use-teams"
import { queryKeys } from "@/lib/api/query-keys"

export type ActiveTeamKind = 'dealership' | 'bodyshop' | null

interface OrganizationContextType {
  // Group-level
  activeGroup: Store | null
  activeGroupId: string | null
  availableGroups: Store[]
  groupDealerships: Store[]
  isGroupView: boolean
  setActiveGroup: (groupId: string) => void
  setGroupView: (isGroupView: boolean) => void

  // Dealership-level
  activeDealership: Store | null
  activeDealershipId: string | null
  availableDealerships: Store[]

  // Body shop
  activeBodyShop: Store | null
  activeBodyShopId: string | null
  availableBodyShops: Store[]

  // Which team the user is currently acting as (dealership vs. bodyshop).
  // activeTeamId resolves to the id of whichever slot matches activeTeamKind.
  activeTeamKind: ActiveTeamKind
  activeTeamId: string | null

  // Shared
  isLoading: boolean
  setActiveDealership: (dealershipId: string) => void
  setActiveBodyShop: (bodyShopId: string) => void
  setActiveTeam: (kind: 'dealership' | 'bodyshop', id: string) => void
  refreshDealerships: () => Promise<void>
  canSwitchDealerships: boolean
  canSwitchBodyShops: boolean
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // Server data via TanStack Query
  const { data: teamsData, isLoading: teamsLoading } = useMyTeams()
  const availableGroups = teamsData?.groups ?? []
  const availableDealerships = teamsData?.dealerships ?? []
  const availableBodyShops = teamsData?.bodyShops ?? []

  // Active selections (client state, persisted in localStorage)
  const [activeGroup, setActiveGroupState] = useState<Store | null>(null)
  const [activeDealership, setActiveDealershipState] = useState<Store | null>(null)
  const [activeBodyShop, setActiveBodyShopState] = useState<Store | null>(null)
  const [activeTeamKind, setActiveTeamKindState] = useState<ActiveTeamKind>(() => {
    const stored = localStorage.getItem("etx_active_team_kind")
    return stored === 'bodyshop' || stored === 'dealership' ? stored : null
  })
  const [isGroupView, setIsGroupViewState] = useState(() => {
    const stored = localStorage.getItem("etx_group_view")
    return stored !== 'false'
  })

  // Group dealerships via TanStack Query
  const { data: groupDealerships = [], isLoading: groupDealershipsLoading } =
    useGroupDealerships(activeGroup?.id)

  // After teamsData lands there's a one-render gap before the post-mount effect
  // below populates activeGroup/activeDealership/activeBodyShop from localStorage.
  // Treat that window as "still loading" so consumers don't make decisions
  // (e.g. settings.tsx's URL fix-up) on a transient empty active-context state.
  const hasMemberships =
    !!teamsData &&
    (teamsData.groups.length > 0 ||
      teamsData.dealerships.length > 0 ||
      teamsData.bodyShops.length > 0)
  const hasActiveContext = !!activeGroup || !!activeDealership || !!activeBodyShop
  const isHydratingActive = hasMemberships && !hasActiveContext

  const isLoading =
    teamsLoading || (!!activeGroup && groupDealershipsLoading) || isHydratingActive

  // Switcher gates use the *user's full set of memberships*, not the active
  // team's role: a Director is allowed to switch between dealerships even
  // while currently acting on one of them, and we want this to be true on
  // first render before any role lookup resolves. Falls back to whether the
  // user has more than one of each kind, which is a reasonable proxy that
  // doesn't need network state. Super-admins always pass.
  const canSwitchDealerships =
    !!user?.admin || availableDealerships.length > 1 || (availableGroups.length > 0 && availableDealerships.length >= 1)
  const canSwitchBodyShops = !!user?.admin || availableBodyShops.length > 1 || availableGroups.length > 0

  // Initialize active selections when teams data first loads
  useEffect(() => {
    if (!teamsData) return

    const { groups, dealerships, bodyShops } = teamsData

    // Set active group
    if (groups.length > 0 && !activeGroup) {
      const storedGroupId = localStorage.getItem("etx_active_group_id")
      const storedGroup = storedGroupId ? groups.find(g => g.id === storedGroupId) : null
      const grp = storedGroup || groups[0]
      setActiveGroupState(grp)
      localStorage.setItem("etx_active_group_id", grp.id)
    } else if (groups.length === 0 && dealerships.length > 0 && !activeDealership) {
      // Non-group user: set active dealership from direct memberships
      const storedId = localStorage.getItem("etx_active_dealership_id")
      const stored = storedId ? dealerships.find(d => d.id === storedId) : null
      const active = stored || dealerships[0]
      setActiveDealershipState(active)
      localStorage.setItem("etx_active_dealership_id", active.id)
    }

    // Set active body shop
    if (bodyShops.length > 0 && !activeBodyShop) {
      const storedId = localStorage.getItem("etx_active_bodyshop_id")
      const stored = storedId ? bodyShops.find(b => b.id === storedId) : null
      const active = stored || bodyShops[0]
      setActiveBodyShopState(active)
      localStorage.setItem("etx_active_bodyshop_id", active.id)
    }

    // Default activeTeamKind once teams data is known: prefer dealership over bodyshop.
    if (!activeTeamKind) {
      if (dealerships.length > 0) {
        setActiveTeamKindState('dealership')
        localStorage.setItem("etx_active_team_kind", 'dealership')
      } else if (bodyShops.length > 0) {
        setActiveTeamKindState('bodyshop')
        localStorage.setItem("etx_active_team_kind", 'bodyshop')
      }
    }
  }, [teamsData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Set active dealership for group users from their direct memberships.
  //
  // We validate against `availableDealerships` (the caller's actual memberships
  // from GET /v1/teams), NOT `groupDealerships` (the group's full org-chart
  // from GET /v1/teams/{groupId}/group/dealerships). Using groupDealerships here is
  // a permission-leak: a Director who's a member of only 4 of 10 child
  // dealerships could land with `activeDealership` pointing at one they're
  // not a member of, causing every team-scoped query (members, estimates,
  // settings) to 403.
  //
  // Self-heals stale localStorage too: if the saved id is no longer in the
  // caller's memberships (membership revoked, dealership deleted, etc.) we
  // fall back to the first available team rather than restoring the dead id.
  useEffect(() => {
    if (!activeGroup || activeDealership) return
    if (availableDealerships.length === 0) return
    const storedId = localStorage.getItem("etx_active_dealership_id")
    const stored = storedId
      ? availableDealerships.find(d => d.id === storedId)
      : null
    const active = stored || availableDealerships[0]
    setActiveDealershipState(active)
    localStorage.setItem("etx_active_dealership_id", active.id)
  }, [availableDealerships, activeGroup]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selections when user logs out — but ONLY on a real logout (we had a
  // user, now we don't), never on the transient `user === null` during the
  // initial auth load. Clearing on that transient nulled activeTeamKind, and the
  // teamsData effect then defaulted it to 'dealership' — silently throwing a
  // Director who'd selected the body shop back to a dealership on every reload.
  const hadUserRef = useRef(false)
  useEffect(() => {
    if (user) {
      hadUserRef.current = true
      return
    }
    if (hadUserRef.current) {
      hadUserRef.current = false
      setActiveGroupState(null)
      setActiveDealershipState(null)
      setActiveBodyShopState(null)
      setActiveTeamKindState(null)
    }
  }, [user])

  const setActiveGroup = (groupId: string) => {
    const group = availableGroups.find(g => g.id === groupId)
    if (group) {
      setActiveGroupState(group)
      setActiveDealershipState(null) // Reset — will be set when group dealerships load
      localStorage.setItem("etx_active_group_id", groupId)
    }
  }

  const setGroupView = (val: boolean) => {
    setIsGroupViewState(val)
    localStorage.setItem("etx_group_view", String(val))
  }

  const setActiveDealership = (dealershipId: string) => {
    // Validate against the caller's actual memberships only — never
    // groupDealerships. The latter is the group's full org-chart and
    // includes teams the caller isn't a member of; setting one of those
    // active would cascade 403s through every team-scoped query.
    const dealership = availableDealerships.find(d => d.id === dealershipId)
    if (dealership) {
      setActiveDealershipState(dealership)
      localStorage.setItem("etx_active_dealership_id", dealershipId)
      setActiveTeamKindState('dealership')
      localStorage.setItem("etx_active_team_kind", 'dealership')
    }
  }

  const setActiveBodyShop = (bodyShopId: string) => {
    const bodyShop = availableBodyShops.find((b) => b.id === bodyShopId)
    if (bodyShop) {
      setActiveBodyShopState(bodyShop)
      localStorage.setItem("etx_active_bodyshop_id", bodyShopId)
      setActiveTeamKindState('bodyshop')
      localStorage.setItem("etx_active_team_kind", 'bodyshop')
    }
  }

  const setActiveTeam = (kind: 'dealership' | 'bodyshop', id: string) => {
    if (kind === 'dealership') {
      setActiveDealership(id)
      setGroupView(false)
    } else {
      setActiveBodyShop(id)
      setGroupView(false)
    }
  }

  const activeTeamId =
    activeTeamKind === 'dealership' ? activeDealership?.id ?? null
    : activeTeamKind === 'bodyshop' ? activeBodyShop?.id ?? null
    : null

  const refreshDealerships = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.teams.all })
  }, [queryClient])

  return (
    <OrganizationContext.Provider
      value={{
        activeGroup,
        activeGroupId: activeGroup?.id || null,
        availableGroups,
        groupDealerships,
        isGroupView: availableGroups.length > 0 && isGroupView,
        setActiveGroup,
        setGroupView,
        activeDealership,
        activeDealershipId: activeDealership?.id || null,
        availableDealerships,
        activeBodyShop,
        activeBodyShopId: activeBodyShop?.id || null,
        availableBodyShops,
        activeTeamKind,
        activeTeamId,
        isLoading,
        setActiveDealership,
        setActiveBodyShop,
        setActiveTeam,
        refreshDealerships,
        canSwitchDealerships,
        canSwitchBodyShops,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider")
  }
  return context
}
